// Ludo board geometry — mirrors ludo_client.php verbatim (1-indexed 15x15 grid,
// rows 1..15, cols 1..15). Used by the React UI to place tokens on the mesh
// grid overlay that sits on top of LudoBoard_BG.png.

export type Color = 'red' | 'green' | 'yellow' | 'blue';

export const YARD = -1;
export const TRACK_LENGTH = 52;
// In the PHP client, token.pos goes: -1 yard, 0..50 track, 51..55 home-stretch (5 cells),
// 56 center (home). We mirror that exactly.
export const STRETCH_START = 51;
export const STRETCH_END = 55;
export const HOME_POSITION = 56;

// 52-cell shared track, copied from PHP trackCoords (1-indexed [row, col]).
export const TRACK_COORDS: [number, number][] = [
  [7, 2], [7, 3], [7, 4], [7, 5], [7, 6],
  [6, 7], [5, 7], [4, 7], [3, 7], [2, 7], [1, 7],
  [1, 8], [1, 9],
  [2, 9], [3, 9], [4, 9], [5, 9], [6, 9],
  [7, 10], [7, 11], [7, 12], [7, 13], [7, 14], [7, 15],
  [8, 15], [9, 15],
  [9, 14], [9, 13], [9, 12], [9, 11], [9, 10],
  [10, 9], [11, 9], [12, 9], [13, 9], [14, 9], [15, 9],
  [15, 8], [15, 7],
  [14, 7], [13, 7], [12, 7], [11, 7], [10, 7],
  [9, 6], [9, 5], [9, 4], [9, 3], [9, 2], [9, 1],
  [8, 1], [7, 1],
];

// 5-cell home stretch per colour, copied from PHP homeCoords.
export const HOME_COORDS: Record<Color, [number, number][]> = {
  red:    [[8, 2], [8, 3], [8, 4], [8, 5], [8, 6]],
  green:  [[2, 8], [3, 8], [4, 8], [5, 8], [6, 8]],
  yellow: [[8, 14], [8, 13], [8, 12], [8, 11], [8, 10]],
  blue:   [[14, 8], [13, 8], [12, 8], [11, 8], [10, 8]],
};

export const SAFE_ZONES = new Set([0, 8, 13, 21, 26, 34, 39, 47]);

export const COLOR_CONFIG: Record<Color, { hex: string; startIndex: number }> = {
  red:    { hex: '#E53935', startIndex: 0 },
  green:  { hex: '#43A047', startIndex: 13 },
  yellow: { hex: '#FDD835', startIndex: 26 },
  blue:   { hex: '#1E88E5', startIndex: 39 },
};

// Board rotation so the local player's home yard is always at the bottom-left.
export const BOARD_ROTATION: Record<Color, number> = {
  blue: 0,
  red: 270,
  green: 180,
  yellow: 90,
};

// Panel ordering: local player → pos-bl, then clockwise (in canonical order
// blue, red, green, yellow) to pos-tl, pos-tr, pos-br.
export const PANEL_ORDER: Color[] = ['blue', 'red', 'green', 'yellow'];

export const COLOR_ORDER: Record<number, Color[]> = {
  2: ['blue', 'green'],
  3: ['blue', 'red', 'green'],
  4: ['blue', 'red', 'green', 'yellow'],
};

// Back-compat re-exports for any call sites still using the old names.
export const COLOR_HEX = Object.fromEntries(
  (Object.entries(COLOR_CONFIG) as [Color, { hex: string; startIndex: number }][]).map(
    ([c, v]) => [c, v.hex],
  ),
) as Record<Color, string>;

export function getAbsolutePos(color: Color, pos: number): number {
  if (pos < 0 || pos > 50) return -1;
  return (COLOR_CONFIG[color].startIndex + pos) % TRACK_LENGTH;
}
